import debug from 'webmd-debug';

/* eslint-disable import/prefer-default-export */
export const videoMetricsMixin = {
	data() {
		return {
			milestones: [25, 50, 75],
			triggeredMilestones: new Set(),
			lastProgress: 0,
		};
	},

	methods: {
		/**
		 * Updates the page data for analytics tracking.
		 * This method sets the 'omniture' data key with page-related information.
		 */
		updatePageData() {
			const { mode } = this;
			const validMode = mode !== 'in-article' && mode !== 'interactive';

			if (validMode) {
				this.store.cms.setPageDataByKey({
					key: 'omniture',
					data: {
						s_pagename: this.pageData?.pageName,
						s_contains_video: mode,
					},
				});
			}
		},

		/**
		 * Calculates and returns the current progress details of the video player.
		 * @param {Object} player - The video player instance.
		 * @returns {Object} - An object containing duration, currentTime,
		 * remainingTime, and percent progress.
		 */
		getVideoProgress(player) {
			const { duration } = player.player;
			const currentTime = Math.round(player.data);

			return {
				duration,
				currentTime,
				remainingTime: duration - currentTime,
				percent: (currentTime / duration) * 100,
			};
		},

		/**
		 * Handles video duration changes and triggers milestone events.
		 * @param {Object} player - The video player instance.
		 */
		handleDurationChange(player) {
			const { currentTime, remainingTime, percent } = this.getVideoProgress(player);
			this.remainingTime = remainingTime;

			// Hide countdown timer after 10 seconds of playback
			if (currentTime >= 10 && this.showCountdownTimer) {
				this.showCountdownTimer = false;
			}

			if (currentTime === 5 && this.noTranscriptAvailable) {
				player.player.settings.change({
					captions: {
						visible: false,
					},
				});
			}

			// Ignore backward scrubbing to avoid duplicate event triggering
			if (percent < this.lastProgress) return;

			// Trigger milestone events sequentially
			this.triggerSequentialMilestones(this.lastProgress, percent);
			this.lastProgress = percent;
		},

		/**
		 * Triggers milestone events for the video progress between given start and end percentages.
		 * @param {number} startPercent - The starting percentage of video progress.
		 * @param {number} endPercent - The ending percentage of video progress.
		 */
		triggerSequentialMilestones(startPercent, endPercent) {
			this.milestones.forEach((milestone) => {
				if (milestone > startPercent && milestone <= endPercent
					&& !this.triggeredMilestones.has(milestone)) {
					this.emitter.emit(`vp-${milestone}pct`, this.ampPlayer);
					debug('[vueVideoPlayer] Emit Event:', `vp-${milestone}pct`);
					this.triggeredMilestones.add(milestone);
				}
			});
		},

		/**
		 * Binds click event listeners to video player elements to capture interactions.
		 */
		bindClickMetrics() {
			const videoContainer = document.getElementById(this.video.container);
			const vuePlayer = videoContainer.querySelector('.vue-video-player');
			const ampLayer = videoContainer.querySelector('.amp-react');
			const ampInteractive = videoContainer.querySelector('.amp-interactive');
			const playPauseIcon = videoContainer.querySelector('.amp-playpause');
			const pauseOverlay = videoContainer.querySelector('.amp-pause-overlay');
			const unmuteOverlay = videoContainer.querySelector('.amp-unmute');

			/**
			 * Handles play and pause actions based on the current state of the player.
			 */
			const handlePlayPause = () => {
				if (!ampLayer.classList.contains('amp-playing')) {
					this.callAdhoc('play', '-ctl');
					this.multiplayerPlayPause('play');
					debug('[vueVideoPlayer] Play');
				} else if (!ampLayer.classList.contains('amp-paused')) {
					this.callAdhoc('pause', '-ctl');
					this.multiplayerPlayPause('pause');
					this.showCountdownTimer = false;
					debug('[vueVideoPlayer] Pause');
				}
			};

			// Attach click event listener to the video container
			videoContainer.addEventListener('click', (event) => {
				const { target } = event;

				// if click target is play/pause overlay
				if (target === pauseOverlay && target !== ampInteractive) {
					handlePlayPause();
				}

				// if click target is the amp overlay
				if (target === ampInteractive) {
					/*
					 * if this is a touch device, clicking the overlay
					 * sometimes triggers a hover event
					 * trigger a click on play/pause overlay as well
					 */
					if (vuePlayer.classList.contains('amp-touch')) {
						if (pauseOverlay) {
							pauseOverlay.click();
						}
					} else if (!ampLayer.classList.contains('amp-ended')) {
						handlePlayPause();
					}
				}

				if (target === playPauseIcon) {
					const action = playPauseIcon.getAttribute('aria-label') === 'Play' ? 'play' : 'pause';

					this.callAdhoc(action, '-ctl');
					this.multiplayerPlayPause(action);
					this.showCountdownTimer = action === 'pause' ? false : this.showCountdownTimer;
					debug(`[vueVideoPlayer] ${action.charAt(0).toUpperCase() + action.slice(1)}`);
				}

				if (target === unmuteOverlay) {
					if (this.config.inviewAutoplay) {
						window.dynVideoState[this.config.dynVidId].audioState = 'unmuted';
					}
					this.callAdhoc('adunmute', '-ctl');
					this.config.muted = false;
					this.config.autoplayPolicy = 'none';
				}

				if (target.classList.contains('amp-cc')) {
					this.isUserClickedCC = true;
				}

				if (target.classList.contains('amp-mute')) {
					this.isUserClickedMute = true;
				}
			}, { passive: true });
		},

		/**
		 * Fires metric calls for user interactions with video modules.
		 * @param {string} mlink - The module link representing the action.
		 * @param {string} ctl - Control suffix added to the module name for tracking.
		 */
		callAdhoc(mlink, ctl) {
			const link = {
				link: `${this.config.moduleName}${ctl}_${mlink}`,
			};

			if (this.mode === 'fed') {
				link.pageName = this.pageData?.pageName;
			}

			this.$vwmdPageLink(
				link,
				{
					metrics: {
						'wb.vdname': this.config.media.title,
					},
				},
			);
		},
	},
};
